import { useQuery } from '@tanstack/react-query';
import { toDateKey } from '@lms/shared';
import { api } from './api';

/**
 * The learning-activity chart's window.
 *
 * The chart used to be a fixed twelve months. Eleven of those bars were
 * history and the only one that could still move was a twelfth of the width,
 * which is the wrong shape for "how am I doing". It opens on this month now,
 * with the other windows a click away.
 */

export type ActivityGranularity = 'DAY' | 'MONTH';

export interface ActivityBucket {
  key: string;
  /** The axis tick. Empty where the server suppressed it to avoid crowding. */
  label: string;
  tooltip: string;
  minutes: number;
  hours: number;
}

export interface ActivityRange {
  from: string;
  to: string;
  granularity: ActivityGranularity;
  buckets: ActivityBucket[];
  totalMinutes: number;
  totalHours: number;
  /** Against the equal-length window immediately before this one. */
  changePercent: number | null;
  /**
   * That preceding window, named and totalled — see the API's note.
   *
   * Rendered beside the headline figure rather than only as a percentage,
   * because the percentage is null whenever the previous window was empty and
   * the comparison then disappeared entirely.
   */
  previous: { from: string; to: string; minutes: number };
  activeDays: number;
  bestDayMinutes: number;
}

export type RangePreset = 'THIS_MONTH' | 'LAST_MONTH' | 'SIX_MONTHS' | 'CUSTOM';

export const RANGE_LABELS: Record<RangePreset, string> = {
  THIS_MONTH: 'This month',
  LAST_MONTH: 'Last month',
  SIX_MONTHS: 'Past 6 months',
  CUSTOM: 'Custom range',
};

/**
 * `YYYY-MM-DD` from a local date — never `toISOString`, which shifts the day.
 *
 * Kept as a name this app already imports in a dozen places, but the rule now
 * lives in `@lms/shared` so the API's export filenames answer with the same
 * day this does. Two implementations of one calendar is how one of them ends
 * up a day out.
 */
export const toDateInput = toDateKey;

/**
 * The dates behind each preset.
 *
 * "This month" runs to today rather than to the end of the month: a row of
 * empty bars for days that have not happened yet reads as time nobody spent,
 * which is not the same thing.
 */
/**
 * What the figure beside the headline is being compared against, in words.
 *
 * The comparison used to be spelled out only as dates — "from 0h in 1–25 Aug"
 * — because for a month-to-date chart the previous window was the equally long
 * run of days ENDING the day before, which is the back end of last month and
 * not a month anybody could name.
 *
 * That is no longer true: a window starting on the first of a month is now
 * compared against the SAME DAYS of the month before, so "last month" is an
 * accurate description rather than a convenient one. See `previousWindow` on
 * the API.
 *
 * The phrase follows the PRESET, because "last month" is only right for one of
 * them — picking "Last month" compares against the month before that, and a
 * custom fortnight against the fortnight before it. The exact dates stay in
 * the tooltip, so the claim is always checkable.
 */
export function comparisonPhrase(preset: RangePreset): string {
  switch (preset) {
    case 'THIS_MONTH':
      return 'last month';
    case 'LAST_MONTH':
      return 'the month before';
    case 'SIX_MONTHS':
      return 'the 6 months before';
    default:
      return 'the period before';
  }
}

/**
 * What the headline total covers — the twin of `comparisonPhrase`.
 *
 * "17m" on its own is a quantity with no period attached, and the card's own
 * range picker is the only thing that said which one. Naming it beside the
 * figure means the sentence reads without looking up at the control: "17m
 * this month, 35% less than 26m last month".
 *
 * A custom range gets no phrase at all rather than a vague one. The picker
 * already shows the dates somebody chose themselves, and "in the selected
 * period" is words that add nothing.
 */
export function periodPhrase(preset: RangePreset): string {
  switch (preset) {
    case 'THIS_MONTH':
      return 'this month';
    case 'LAST_MONTH':
      return 'last month';
    case 'SIX_MONTHS':
      return 'over 6 months';
    default:
      return '';
  }
}

export interface ActivityDelta {
  /** `+200%`, or `+50m` where a percentage would be dividing by nothing. */
  label: string;
  direction: 'up' | 'down' | 'flat';
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * THE CHANGE, AS SOMETHING THAT CAN ALWAYS BE SAID
 *
 * `changePercent` is null whenever the previous window was too small to
 * divide by, and on a young account that is most months — so the card showed
 * "50m this month / 0m last month" and no movement at all, which reads as a
 * feature that does not work rather than as a month with no baseline.
 *
 * A ratio is genuinely undefined against zero. A DIFFERENCE is not: fifty
 * minutes more than none is exactly fifty minutes more, and it is the figure
 * the learner would use themselves. So the percentage is preferred when it
 * can be trusted and the absolute difference stands in when it cannot — the
 * same answer the Active learners tile arrived at, for the same reason.
 *
 * Null only when there is genuinely nothing to report: no time this window
 * and none before it. "0m, unchanged from 0m" is noise on a card whose job is
 * to encourage somebody to start.
 * ═══════════════════════════════════════════════════════════════════════════
 */
export function activityDelta(
  totalMinutes: number,
  previousMinutes: number,
  changePercent: number | null,
): ActivityDelta | null {
  const difference = totalMinutes - previousMinutes;

  /* Nothing either side. Not a flat line — an empty one. */
  if (difference === 0 && totalMinutes === 0) return null;

  const direction: ActivityDelta['direction'] =
    difference > 0 ? 'up' : difference < 0 ? 'down' : 'flat';

  if (changePercent !== null) {
    const rounded = Math.round(changePercent);
    return { label: `${rounded > 0 ? '+' : ''}${rounded}%`, direction };
  }

  /* No trustworthy baseline, so state the amount rather than a ratio of it.
     `formatMinutes` never prints a sign, so it goes on the magnitude and the
     sign is added here — "+50m", and "-12m" rather than "+-12m". */
  if (difference === 0) return { label: 'no change', direction };

  return {
    label: `${difference > 0 ? '+' : '-'}${formatMinutes(Math.abs(difference))}`,
    direction,
  };
}

export function presetRange(preset: Exclude<RangePreset, 'CUSTOM'>, now = new Date()) {
  switch (preset) {
    case 'THIS_MONTH':
      return {
        from: new Date(now.getFullYear(), now.getMonth(), 1),
        to: new Date(now.getFullYear(), now.getMonth(), now.getDate()),
      };
    case 'LAST_MONTH':
      return {
        from: new Date(now.getFullYear(), now.getMonth() - 1, 1),
        // Day 0 of this month is the last day of the previous one, whatever
        // its length and whether or not it is a leap February.
        to: new Date(now.getFullYear(), now.getMonth(), 0),
      };
    case 'SIX_MONTHS':
      return {
        from: new Date(now.getFullYear(), now.getMonth() - 5, 1),
        to: new Date(now.getFullYear(), now.getMonth(), now.getDate()),
      };
  }
}

export function useActivityRange(
  from: string,
  to: string,
  /** Skipped while the inline first-paint data is still the right window. */
  enabled: boolean,
) {
  return useQuery({
    queryKey: ['dashboard', 'activity', from, to],
    queryFn: async () => {
      const { data } = await api.get<ActivityRange>('/dashboard/activity', {
        params: { from, to },
      });
      return data;
    },
    enabled,
    // The window rarely changes under the user, and flicking between presets
    // should feel instant on the second visit.
    staleTime: 60_000,
  });
}

/** "0h" / "40m" / "3.5h" — the same shape the roster uses. */
export function formatMinutes(minutes: number): string {
  /**
   * `0m`, not `0h`.
   *
   * Zero is zero either way, but this figure is almost always shown BESIDE
   * another one — "2m, up from 0h last month" reads as a change of unit as
   * well as of quantity, and takes a second to parse as "up from nothing".
   * Matching the unit of the small end of the scale removes the stumble.
   */
  if (minutes <= 0) return '0m';
  if (minutes < 60) return `${minutes}m`;
  const hours = minutes / 60;
  return `${hours < 10 ? Math.round(hours * 10) / 10 : Math.round(hours)}h`;
}
